<template>
  <div>
    <sweet-modal
      ref="organizeModal"
      class="instagram-feed-organize-modal"
      width="560px"
      :title="$t('ct-instagram-organize-title')"
    >
      <div class="ig-organize">
        <div class="ig-organize__frame">
          <p v-if="!items.length" class="ig-organize__empty">
            {{ $t('ct-instagram-organize-empty') }}
          </p>
          <draggable
            v-else
            v-model="items"
            class="ig-organize__grid"
            handle=".ig-organize__move"
            ghost-class="ig-organize__ghost"
            :animation="180"
          >
            <div
              v-for="item in items"
              :key="item.id"
              class="ig-organize__tile"
              :class="{ 'is-hidden': item.hidden }"
            >
              <img
                class="ig-organize__image"
                :src="item.imageSrc"
                :alt="item.caption || 'Instagram post'"
              />
              <div class="ig-organize__overlay">
                <button
                  :aria-label="$t('ct-instagram-move-post')"
                  type="button"
                  class="ig-organize__action ig-organize__move"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M11 4h2v4.17l1.59-1.58L16 8l-4 4-4-4 1.41-1.41L11 8.17V4zm9 7v2h-4.17l1.58 1.59L16 16l-4-4 4-4 1.41 1.41L15.83 11H20zM4 11h4.17l-1.58-1.59L8 8l4 4-4 4-1.41-1.41L8.17 13H4v-2zm7 9h2v-4.17l1.59 1.58L16 16l-4-4-4 4 1.41 1.41L11 15.83V20z"/>
                  </svg>
                </button>
                <button
                  type="button"
                  class="ig-organize__action"
                  :aria-label="item.hidden ? $t('ct-instagram-show-post') : $t('ct-instagram-hide-post')"
                  @click.stop="toggleHidden(item)"
                >
                  <svg v-if="item.hidden" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M12 6a9.77 9.77 0 018.82 5.5 9.77 9.77 0 01-8.82 5.5 9.77 9.77 0 01-8.82-5.5A9.77 9.77 0 0112 6m0-2C7 4 2.73 7.11 1 11.5 2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4zm0 5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z"/>
                    <path fill="currentColor" d="M3.27 4.27L4.73 2.81 21.2 19.27l-1.46 1.46z"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M12 6a9.77 9.77 0 018.82 5.5 9.77 9.77 0 01-8.82 5.5 9.77 9.77 0 01-8.82-5.5A9.77 9.77 0 0112 6m0-2C7 4 2.73 7.11 1 11.5 2.73 15.89 7 19 12 19s9.27-3.11 11-7.5C21.27 7.11 17 4 12 4zm0 5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z"/>
                  </svg>
                </button>
              </div>
            </div>
          </draggable>
        </div>
      </div>

      <button slot="button" type="button" class="btn btn-link ig-organize-cancel" @click="cancel">
        {{ $t('ct-cancel') }}
      </button>
      <button slot="button" type="button" class="btn btn-success" @click="save">
        <i class="svg-icon svgi-save"></i>
        {{ $t('ct-save') }}
      </button>
    </sweet-modal>
  </div>
</template>

<script>
import { SweetModal } from 'sweet-modal-vue';
import Draggable from 'vuedraggable';
import { applyCustomPostOrder, orderFeedPosts } from './layoutPresets';

export default {
  name: 'InstagramFeedOrganizePostsModal',
  components: {
    SweetModal,
    Draggable,
  },
  data() {
    return {
      items: [],
      onSaveCallback: null,
    };
  },
  mounted() {
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }
  },
  beforeDestroy() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    open(posts, config, seed, onSave) {
      this.onSaveCallback = onSave || null;
      this.items = this.buildItems(posts, config, seed);
      if (this.$refs.organizeModal) {
        this.$refs.organizeModal.open();
      }
    },
    buildItems(posts, config, seed) {
      const source = Array.isArray(posts) ? posts : [];
      const hidden = {};
      ((config && config.hiddenPostIds) || []).forEach((id) => {
        hidden[String(id)] = true;
      });
      const ordered = applyCustomPostOrder(
        orderFeedPosts(source, config && config.postOrder, seed),
        config && config.customPostOrder,
      );
      return ordered.filter((post) => post && post.id).map((post) => ({
        id: String(post.id),
        caption: post.caption || '',
        imageSrc: this.postImage(post),
        hidden: !!hidden[String(post.id)],
      }));
    },
    postImage(post) {
      if (post.mediaType === 'VIDEO' || post.mediaType === 'REELS') {
        return post.thumbnailUrl || post.mediaUrl;
      }
      return post.mediaUrl || post.thumbnailUrl;
    },
    toggleHidden(item) {
      item.hidden = !item.hidden;
    },
    cancel() {
      this.close();
    },
    save() {
      if (typeof this.onSaveCallback === 'function') {
        this.onSaveCallback({
          customPostOrder: this.items.map((item) => item.id),
          hiddenPostIds: this.items.filter((item) => item.hidden).map((item) => item.id),
        });
      }
      this.close();
    },
    close() {
      if (this.$refs.organizeModal) {
        this.$refs.organizeModal.close();
      }
    },
  },
};
</script>

<style scoped>
.ig-organize {
  text-align: left;
  padding-top: 4px;
}
.ig-organize__frame {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  max-height: 360px;
  overflow-y: auto;
  box-sizing: border-box;
}
.ig-organize__empty {
  margin: 24px 8px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}
.ig-organize__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.ig-organize__tile {
  position: relative;
  padding-bottom: 100%;
  height: 0;
  overflow: hidden;
  border-radius: 4px;
  background: #e2e8f0;
  cursor: default;
}
.ig-organize__image {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ig-organize__overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(15, 23, 42, 0.35);
  opacity: 0;
  transition: opacity 0.15s ease;
}
.ig-organize__tile:hover .ig-organize__overlay,
.ig-organize__tile.is-hidden .ig-organize__overlay {
  opacity: 1;
}
.ig-organize__tile.is-hidden .ig-organize__image {
  filter: grayscale(0.2);
  opacity: 0.55;
}
.ig-organize__action {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
}
.ig-organize__move {
  cursor: move;
}
.ig-organize__action:hover {
  background: rgba(255, 255, 255, 0.18);
}
</style>

<style lang="scss">
.instagram-feed-organize-modal {
  .sweet-content {
    padding: 16px 24px 8px !important;
    box-sizing: border-box;
  }

  .sweet-buttons {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center;
    width: 100%;
    padding: 8px 20px 12px !important;
  }

  .btn {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .ig-organize-cancel {
    color: #64748b !important;
    background: transparent !important;
    border: 0 !important;
    box-shadow: none !important;
    min-height: auto !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/editor/apps/InstagramFeed/OrganizePostsModal.vue